import React, { useState, useEffect } from 'react'; // Added useEffect import
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  IconButton,
  CardMedia,
  Divider,
  alpha,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade // Added Fade import
} from '@mui/material';
import {
  ChatBubble,
  ThumbUp,
  ThumbDown,
  Share,
  Sort,
  SortOutlined,
  VolumeUp,
  VolumeOff,
  PostAdd,
  MoreVert, 
  Edit, 
  Flag, 
  Delete,
  KeyboardArrowUp // Added KeyboardArrowUp import
} from '@mui/icons-material';
import { Post } from '../../types/community';
import toast from 'react-hot-toast';
import EditPostModal from './EditPostModal';
import postApi from '../../services/postApi';
import ReportPostModal from '../Admin/ReportPostModal';
import { PostListSkeleton } from '../skeletons'; 

interface PostListProps {
  posts: Post[];
  postsLoading: boolean;
  activeTab: number;
  postSortBy: 'desc' | 'asc';
  setPostSortBy: (sort: 'desc' | 'asc') => void;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  user: any;
  handleCreatePost: () => void;
  handleVote: (postId: string, vote: 'up' | 'down' | 'remove') => void;
  handleVideoRef: (postId: string, el: HTMLVideoElement | null) => void;
  toggleVideoMute: (postId: string) => void;
  refreshPosts: () => void;
  videoMuted: { [key: string]: boolean };
  navigate: (path: string) => void;
  glassEffect: any;
  gradientButtonStyle: any;
  // New prop for top comments
  topComments?: {
    [postId: string]: {
      id: string;
      content: string;
      author: {
        id: string;
        username: string;
        profilePicture?: string;
      };
      createdAt: string;
      upvotes: number;
      downvotes: number;
    };
  };
}

const PostList: React.FC<PostListProps> = ({
  posts,
  postsLoading,
  activeTab,
  postSortBy,
  setPostSortBy,
  isDark,
  goldColor,
  deepGreen,
  user,
  handleCreatePost,
  handleVote,
  handleVideoRef,
  toggleVideoMute,
  videoMuted,
  navigate,
  glassEffect,
  gradientButtonStyle,
  topComments = {} // Default to empty object
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleUserProfileClick = (username: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); 
    }
    if (username) {
      navigate(`/${username}`);
    }
  };
  
  // State for scroll to top button
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Scroll event handler - Show arrow when scrolled
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Show button when scrolled more than 1.5x viewport height
      setShowScrollTop(scrolled > viewportHeight * 1.5);
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Check on initial load
    handleScroll();

    // Cleanup
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, post: Post) => {
    event.preventDefault();
    event.stopPropagation(); 
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  const handleEditPost = () => {
    if (selectedPost) {
      setEditModalOpen(true);
      handleMenuClose();
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost || !user) return;
    
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      handleMenuClose();
      return;
    }

    try {
      setDeletingPostId(selectedPost.id);
      const response = await postApi.deletePost(selectedPost.id);
      
      if (response.success) {
        toast.success('Post deleted successfully');
        // Remove post from state (you'll need to update this)
        // setPosts(prev => prev.filter(post => post.id !== selectedPost.id));
      } else {
        toast.error(response.error || 'Failed to delete post');
      }
    } catch (error: any) {
      console.error('Delete post error:', error);
      toast.error(error.message || 'Failed to delete post');
    } finally {
      setDeletingPostId(null);
      handleMenuClose();
    }
  };

  const handleSharePost = () => {
    if (selectedPost) {
      navigator.clipboard.writeText(`${window.location.origin}/post/${selectedPost.id}`)
        .then(() => {
          toast.success('Post link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          toast.error('Failed to copy link');
        });
      handleMenuClose();
    }
  };

  const handleReportPost = () => {
  if (selectedPost) {
    setReportModalOpen(true);
  }
  handleMenuClose();
};

const handleReportSuccess = () => {
  toast.success('Report submitted successfully! Our team will review it shortly.');
  setReportModalOpen(false);
};

  const handleEditSuccess = (updatedPost: any) => {
    // Handle post update success
    toast.success('Post updated successfully');
    setEditModalOpen(false);
    // You might want to refresh posts or update the specific post in state
  };

  // Sorting function
const getSortedPosts = () => {
  if (!posts.length) return [];
  
  return [...posts].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    
    return postSortBy === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

// Use sorted posts
const sortedPosts = getSortedPosts();

  // Check if current user is the author of the post
  const isPostAuthor = (post: Post) => {
  return post.isAuthor === true; // Use the isAuthor field from the backend response
};

  const getActiveTabTitle = () => {
    switch (activeTab) {
      case 0: return postSortBy === 'desc' ? 'Latest Posts' : 'Oldest Posts';
      case 1: return 'Community Feed';
      case 2: return 'Trending Posts';
      default: return postSortBy === 'desc' ? 'Latest Posts' : 'Oldest Posts';
    }
  };

  const handlePostClick = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  // Function to format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* Sort Header Card - Different styling for mobile/desktop */}
      {!isMobile ? (
        // Desktop: Card with glass effect
        <Card sx={{ 
          mb: 3, 
          borderRadius: '20px', 
          ...glassEffect,
          width: '100%',
          maxWidth: '680px'
        }}>
          <CardContent sx={{ py: 2.5, px: 3, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              rowGap: 1
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 800, 
                color: isDark ? '#fff' : deepGreen, 
                fontSize: '1.1rem',
                whiteSpace: 'nowrap'
              }}>
                {getActiveTabTitle()}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  fontWeight: 600
                }}>
                  Sort by:
                </Typography>
                <Chip
                  icon={postSortBy === 'desc' ? <Sort /> : <SortOutlined />}
                  label={postSortBy === 'desc' ? 'Newest First' : 'Oldest First'}
                  onClick={() => setPostSortBy(postSortBy === 'desc' ? 'asc' : 'desc')}
                  sx={{
                    height: 36,
                    borderRadius: '20px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.05)',
                    color: isDark ? '#fff' : deepGreen,
                    border: `1px solid ${alpha(goldColor, 0.3)}`,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                      borderColor: goldColor
                    },
                    '& .MuiChip-icon': {
                      color: goldColor,
                      marginLeft: '8px',
                      fontSize: '20px'
                    }
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        // Mobile: Simple box without card styling
        <Box sx={{ 
          mb: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 2,
          width: '100%'
        }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 800, 
            color: isDark ? '#fff' : deepGreen, 
            fontSize: '1rem',
          }}>
            {getActiveTabTitle()}
          </Typography>
          <Chip
            icon={postSortBy === 'desc' ? <Sort /> : <SortOutlined />}
            label={postSortBy === 'desc' ? 'Newest' : 'Oldest'}
            onClick={() => setPostSortBy(postSortBy === 'desc' ? 'asc' : 'desc')}
            sx={{
              height: 32,
              borderRadius: '20px',
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.05)',
              color: isDark ? '#fff' : deepGreen,
              border: `1px solid ${alpha(goldColor, 0.3)}`,
              fontWeight: 600,
              fontSize: '0.85rem',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                borderColor: goldColor
              },
              '& .MuiChip-icon': {
                color: goldColor,
                marginLeft: '6px',
                fontSize: '18px'
              }
            }}
          />
        </Box>
      )}

      {postsLoading ? (
    <PostListSkeleton />
  ) : posts.length === 0 ? (
        // No Posts State - Different styling for mobile/desktop
        !isMobile ? (
          // Desktop
          <Card sx={{ 
            p: 8, 
            textAlign: 'center', 
            borderRadius: '20px', 
            ...glassEffect,
            width: '100%',
            maxWidth: '680px'
          }}>
            <ChatBubble sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
            <Typography variant="h5" sx={{ mb: 2, color: isDark ? '#fff' : deepGreen, fontWeight: 800 }}>No posts yet</Typography>
            <Typography variant="body1" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 4, maxWidth: 400, mx: 'auto' }}>
              Be the first to create a post and start the conversation!
            </Typography>
            {user && (
              <Button
                variant="contained"
                startIcon={<PostAdd />}
                onClick={handleCreatePost}
                sx={{
                  ...gradientButtonStyle,
                  px: 5,
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Create First Post
              </Button>
            )}
          </Card>
        ) : (
          // Mobile
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            width: '100%'
          }}>
            <ChatBubble sx={{ fontSize: 60, color: goldColor, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 2, color: isDark ? '#fff' : deepGreen, fontWeight: 800 }}>No posts yet</Typography>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', mb: 3, px: 2 }}>
              Be the first to create a post and start the conversation!
            </Typography>
            {user && (
              <Button
                variant="contained"
                startIcon={<PostAdd />}
                onClick={handleCreatePost}
                sx={{
                  ...gradientButtonStyle,
                  px: 4,
                  py: 1,
                  fontSize: '0.9rem'
                }}
              >
                Create First Post
              </Button>
            )}
          </Box>
        )
      ) : (
        // Posts Container - Full width on mobile, limited width on desktop
        <Box sx={{
          width: '100%',
          maxWidth: { xs: '100%', md: '680px' },
          margin: { xs: '0', md: '0 auto' },
          position: 'relative'
        }}>
          {sortedPosts.map((post, index) => {
            const topComment = topComments[post.id];
            
            return (
              <React.Fragment key={post.id}>
                {isMobile && index > 0 && (
                  <Divider 
                    sx={{ 
                      mx: 0,
                      my: 0,
                      borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)',
                      borderBottomWidth: '1px'
                    }} 
                  />
                )}
                <Box
                  sx={{
                    mb: isMobile ? 0 : 3,
                    borderRadius: isMobile ? 0 : '16px',
                    overflow: 'hidden',
                    ...(isMobile ? {} : glassEffect),
                    width: '100%',
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover': !isMobile ? {
                      transform: 'translateY(-2px)',
                      boxShadow: isDark 
                        ? '0 20px 60px rgba(0,0,0,0.9)' 
                        : '0 20px 60px rgba(5, 24, 20, 0.2)',
                      transition: 'all 0.2s ease-in-out'
                    } : {}
                  }}
                >
                  {/* Clickable Content Area */}
                  <Box 
                    onClick={() => handlePostClick(post.id)}
                    sx={{ 
                      p: isMobile ? 2 : 3,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Header section with avatar and user info */}
                    {/* Header section with avatar and user info */}
<Box sx={{ 
  display: 'flex', 
  alignItems: 'flex-start', 
  gap: isMobile ? 1.5 : 3, 
  mb: isMobile ? 1.5 : 3 
}}>
  <Avatar
  src={post.author?.profilePicture}
  onClick={(e) => handleUserProfileClick(post.author?.username || '', e)}
  sx={{
    width: isMobile ? 40 : 52,
    height: isMobile ? 40 : 52,
    border: `2px solid ${alpha(goldColor, 0.3)}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {post.author?.username?.charAt(0)?.toUpperCase() || 'U'}
</Avatar>
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isMobile ? 1 : 1.5,
      flexWrap: 'wrap',
      minWidth: 0
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
        <Typography 
  variant="subtitle1" 
  onClick={(e) => handleUserProfileClick(post.author?.username || '', e)}
  sx={{
    fontWeight: 800,
    color: isDark ? '#fff' : deepGreen,
    fontSize: isMobile ? '0.95rem' : '1.1rem',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    // Desktop only: in the narrow three-column layout a username is a single
    // unbreakable token that would push the post header past the column edge.
    // Mobile gets the full page width, so leave its wrapping alone.
    ...(isMobile ? {} : {
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    '&:hover': {
      color: goldColor,
    }
  }}
>
  {post.author?.username || 'Unknown'}
</Typography>
        {post.community && (
          <>
            <Typography variant="caption" sx={{ 
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
              fontSize: isMobile ? '0.7rem' : '0.8rem' 
            }}>in</Typography>
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/community/${post.community?.slug}`);
              }}
              sx={{
                color: goldColor,
                fontWeight: 800,
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                cursor: 'pointer',
                background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                px: isMobile ? 1 : 1.5,
                py: isMobile ? 0.25 : 0.5,
                borderRadius: '10px',
                transition: 'all 0.2s',
                // A long slug is one unbreakable token; let it truncate rather
                // than push the post header past the column edge.
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)',
                  textDecoration: 'none'
                }
              }}
            >
              wfh/{post.community?.slug}
            </Box>
          </>
        )}
      </Box>
      
      {/* Add MoreVert icon button - HIDDEN ON MOBILE */}
      {!isMobile && (
        <IconButton
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    handleMenuOpen(e, post);
  }}
  sx={{
    color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
    padding: '8px',
    '&:hover': {
      color: goldColor,
      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    }
  }}
>
  <MoreVert fontSize="medium" />
</IconButton>
      )}
    </Box>
    <Typography variant="caption" sx={{ 
      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
      fontSize: isMobile ? '0.7rem' : '0.8rem'
    }}>
      {formatRelativeTime(post.createdAt)}
    </Typography>
  </Box>
</Box>

                    {/* Post title */}
                    <Typography variant="h5" sx={{
                      fontWeight: 900,
                      mb: isMobile ? 1.5 : 2.5,
                      lineHeight: isMobile ? 1.3 : 1.4,
                      color: isDark ? '#fff' : deepGreen,
                      fontSize: isMobile ? '1.1rem' : '1.4rem',
                      wordBreak: 'break-word'
                    }}>
                      {post.title}
                    </Typography>

                    {/* Media section */}
                    {(post.type === 'image' || post.type === 'video') && post.mediaUrl && (
                      <Box sx={{
                        mb: isMobile ? 2 : 3,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        maxHeight: '400px'
                      }}>
                        {post.type === 'image' ? (
                          <CardMedia
                            component="img"
                            image={post.mediaUrl}
                            alt="Post"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'
                            }}
                          />
                        ) : (
                          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                            <video
                              ref={(el) => handleVideoRef(post.id, el)}
                              src={post.mediaUrl}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '10px'
                              }}
                              loop
                              autoPlay
                              playsInline
                              muted={videoMuted[post.id] !== false}
                            />
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVideoMute(post.id);
                              }}
                              sx={{
                                position: 'absolute',
                                bottom: 10,
                                right: 10,
                                bgcolor: 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                width: 32,
                                height: 32,
                                fontSize: '0.9rem',
                                '&:hover': {
                                  bgcolor: 'rgba(0,0,0,0.8)'
                                }
                              }}
                            >
                              {videoMuted[post.id] === false ? <VolumeUp /> : <VolumeOff />}
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Post content */}
                    {post.content && (
                      <Typography variant="body1" sx={{
                        mb: isMobile ? 2 : 3,
                        color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                        lineHeight: isMobile ? 1.6 : 1.7,
                        fontSize: isMobile ? '0.95rem' : '1.05rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {post.content.length > (isMobile ? 150 : 300) ? `${post.content.substring(0, isMobile ? 150 : 300)}...` : post.content}
                        {post.content.length > (isMobile ? 150 : 300) && (
                          <Button
                            size="small"
                            sx={{
                              color: goldColor,
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: isMobile ? '0.8rem' : '0.9rem',
                              ml: 0.5,
                              minWidth: 'auto',
                              p: 0.5
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/post/${post.id}`);
                            }}
                          >
                            Read more
                          </Button>
                        )}
                      </Typography>
                    )}

                    {/* Tags section */}
                    {post.tags && post.tags.length > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: isMobile ? 0.5 : 1, 
                        mb: isMobile ? 2 : 3 
                      }}>
                        {post.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={`#${tag}`}
                            size="small"
                            sx={{
                              bgcolor: alpha(goldColor, 0.1),
                              color: goldColor,
                              fontWeight: 600,
                              borderRadius: '10px',
                              fontSize: isMobile ? '0.7rem' : '0.85rem',
                              height: isMobile ? 24 : 32,
                              '&:hover': {
                                bgcolor: alpha(goldColor, 0.2)
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Show Top Comment if exists - ABOVE the action buttons */}
                  {topComment && (
                    <Box 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/post/${post.id}#comment-${topComment.id}`);
                      }}
                      sx={{
                        mx: isMobile ? 2 : 3,
                        mb: 2,
                        p: 2,
                        borderRadius: '15px',
                        bgcolor: isDark ? alpha(goldColor, 0.05) : alpha(goldColor, 0.02),
                        border: `1px solid ${alpha(goldColor, 0.1)}`,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: isDark ? alpha(goldColor, 0.08) : alpha(goldColor, 0.05),
                          borderColor: alpha(goldColor, 0.3)
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar
  src={topComment.author.profilePicture}
  onClick={(e) => handleUserProfileClick(topComment.author.username || '', e)}
  sx={{
    width: 28,
    height: 28,
    border: `1px solid ${alpha(goldColor, 0.3)}`,
    cursor: 'pointer', 
    transition: 'transform 0.2s ease', 
    '&:hover': {
      transform: 'scale(1.1)', 
    }
  }}
>
  {topComment.author.username?.charAt(0).toUpperCase()}
</Avatar>
                        <Typography 
  variant="caption" 
  onClick={(e) => handleUserProfileClick(topComment.author.username || '', e)}
  sx={{ 
    fontWeight: 600, 
    color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
    fontSize: '0.9rem',
    cursor: 'pointer', 
    transition: 'color 0.2s ease', 
    '&:hover': {
      color: goldColor, 
    }
  }}
>
  {topComment.author.username}
</Typography>
                        <Typography variant="caption" sx={{ 
                          color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                          fontSize: '0.8rem'
                        }}>
                          • {formatRelativeTime(topComment.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                        lineHeight: 1.4,
                        fontSize: '0.85rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {topComment.content}
                      </Typography>
                      {/* Show comment votes */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ThumbUp sx={{ fontSize: '0.8rem', color: alpha(goldColor, 0.7) }} />
                          <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                            {topComment.upvotes}
                          </Typography>
                        </Box> */}
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ThumbDown sx={{ fontSize: '0.8rem', color: alpha(goldColor, 0.7) }} />
                          <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                            {topComment.downvotes}
                          </Typography>
                        </Box> */}
                        <Typography variant="caption" sx={{ 
                          color: goldColor,
                          fontWeight: 600,
                          marginLeft: 'auto',
                          fontSize: '0.8rem'
                        }}>
                          View all comments →
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Action buttons - SEPARATE FROM CLICKABLE AREA */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 2,
                    borderTop: `1px solid ${alpha(goldColor, 0.1)}`,
                    px: isMobile ? 2 : 3,
                    pb: isMobile ? 2 : 3
                  }}>
                    {!isMobile ? (
                      // DESKTOP ACTION BUTTONS
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { md: 1, lg: 2 },
                        maxWidth: '100%',
                        minWidth: 0,
                        // Scrollbars are hidden page-wide, so overflow here would be
                        // unreachable. Wrap to a second row in narrow columns instead.
                        flexWrap: 'wrap',
                        rowGap: 1
                      }}>
                        <Button
                          startIcon={<ThumbUp />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up');
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 2,
                            py: 0.5,
                            bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                            ...(post.userVote === 'up' ? {} : glassEffect),
                            fontSize: '0.9rem',
                            '&:hover': {
                              bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                            }
                          }}
                        >
                          {post.upvotes}
                        </Button>

                        <Button
                          startIcon={<ThumbDown />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down');
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 2,
                            py: 0.5,
                            bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                            ...(post.userVote === 'down' ? {} : glassEffect),
                            fontSize: '0.9rem',
                            '&:hover': {
                              bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                            }
                          }}
                        >
                          {post.downvotes}
                        </Button>

                        <Button
                          startIcon={<ChatBubble />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/post/${post.id}`);
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 2,
                            py: 0.5,
                            ...glassEffect,
                            fontSize: '0.9rem',
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.05)
                            }
                          }}
                        >
                          {post.commentsCount}
                        </Button>

                        <Button
                          startIcon={<Share />}
                          size="small"
                          sx={{
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            fontWeight: 600,
                            borderRadius: '20px',
                            px: 2,
                            py: 0.5,
                            fontSize: '0.85rem',
                            ...glassEffect,
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.05)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
                            toast.success('Link copied to clipboard!');
                          }}
                        >
                          Share
                        </Button>
                      </Box>
                    ) : (
                      // MOBILE ACTION BUTTONS
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        width: '100%',
                        justifyContent: 'space-between'
                      }}>
                        <Button
                          startIcon={<ThumbUp sx={{ fontSize: '1.2rem' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up');
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 1.2,
                            py: 0.6,
                            bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                            fontSize: '0.85rem',
                            '&:hover': {
                              bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                            },
                            flex: 1,
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0.6
                            }
                          }}
                        >
                          {post.upvotes}
                        </Button>

                        <Button
                          startIcon={<ThumbDown sx={{ fontSize: '1.2rem' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down');
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 1.2,
                            py: 0.6,
                            bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                            fontSize: '0.85rem',
                            '&:hover': {
                              bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05)
                            },
                            flex: 1,
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0.6
                            }
                          }}
                        >
                          {post.downvotes}
                        </Button>

                        <Button
                          startIcon={<ChatBubble sx={{ fontSize: '1.2rem' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/post/${post.id}`);
                          }}
                          sx={{
                            minWidth: 'auto',
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            fontWeight: 700,
                            borderRadius: '20px',
                            px: 1.2,
                            py: 0.6,
                            fontSize: '0.85rem',
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.05)
                            },
                            flex: 1,
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0.6
                            }
                          }}
                        >
                          {post.commentsCount}
                        </Button>

                        <Button
                          startIcon={<Share sx={{ fontSize: '1.2rem' }} />}
                          size="small"
                          sx={{
                            minWidth: 'auto',
                            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                            fontWeight: 600,
                            borderRadius: '20px',
                            px: 1.2,
                            py: 0.6,
                            fontSize: '0.85rem',
                            '&:hover': {
                              bgcolor: alpha(goldColor, 0.05)
                            },
                            flex: 1,
                            justifyContent: 'center',
                            '& .MuiButton-startIcon': {
                              marginRight: 0.6
                            }
                        }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
                            toast.success('Link copied to clipboard!');
                          }}
                        >
                          Share
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
      
      {/* Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <Button
          onClick={handleScrollToTop}
          startIcon={<KeyboardArrowUp />}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%) translateY(0)',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(20px)',
            color: goldColor,
            zIndex: 1000,
            border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.5)'}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: showScrollTop ? 'float 2s ease-in-out infinite' : 'none',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
            px: 3,
            py: 1,
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)',
              transform: 'translateX(-50%) translateY(-8px)',
              borderColor: goldColor,
            },
            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          Move to Top
        </Button>
      </Fade>

      {/* Add CSS animations */}
      <style>{`
                @keyframes float {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-4px);
          }
        }
      `}</style>

      {/* Menu for post options */}
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={handleMenuClose}
    PaperProps={{
      sx: {
        ...glassEffect,
        borderRadius: '12px',
        mt: 1,
        minWidth: '180px'
      }
    }}
  >
    {/* Share option - visible to all */}
    <MenuItem onClick={handleSharePost}>
      <ListItemIcon>
        <Share fontSize="small" sx={{ color: goldColor }} />
      </ListItemIcon>
      <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Share Post</ListItemText>
    </MenuItem>

    {/* Edit option - only visible to post author */}
{selectedPost && isPostAuthor(selectedPost) && (
  <>
    <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
    <MenuItem onClick={handleEditPost}>
      <ListItemIcon>
        <Edit fontSize="small" sx={{ color: goldColor }} />
      </ListItemIcon>
      <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Edit Post</ListItemText>
    </MenuItem>
  </>
)}

{/* Delete option - only visible to post author */}
{selectedPost && isPostAuthor(selectedPost) && (
  <MenuItem onClick={handleDeletePost} sx={{ color: '#ff4444' }}>
    <ListItemIcon>
      <Delete fontSize="small" sx={{ color: '#ff4444' }} />
    </ListItemIcon>
    <ListItemText>Delete Post</ListItemText>
  </MenuItem>
)}

    <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
    
    {/* Report option - visible to all */}
    <MenuItem onClick={handleReportPost} sx={{ color: '#ff4444' }}>
      <ListItemIcon>
        <Flag fontSize="small" sx={{ color: '#ff4444' }} />
      </ListItemIcon>
      <ListItemText>Report Post</ListItemText>
    </MenuItem>
  </Menu>

  {/* Edit Post Modal */}
  {selectedPost && (
    <EditPostModal
      open={editModalOpen}
      onClose={() => setEditModalOpen(false)}
      onSuccess={handleEditSuccess}
      post={selectedPost}
    />
  )}

  {/* Report Post Modal */}
{selectedPost && (
  <ReportPostModal
    open={reportModalOpen}
    onClose={() => setReportModalOpen(false)}
    onSuccess={handleReportSuccess}
    post={{
      id: selectedPost.id,
      title: selectedPost.title,
      content: selectedPost.content || '',
      author: {
        id: selectedPost.author?.id || '',
        username: selectedPost.author?.username || 'Unknown',
        profilePicture: selectedPost.author?.profilePicture
      },
      createdAt: selectedPost.createdAt,
      community: selectedPost.community ? {
        id: selectedPost.community.id,
        name: selectedPost.community.name,
        slug: selectedPost.community.slug
      } : undefined
    }}
  />
)}
    </>
  );
};

export default PostList;